"use client";
import { API_URL } from "@/lib/config";

// CommentPanel — threaded comments slide-over for PanoramaViewer.
// Supports top-level comments and one level of replies.

import { useEffect, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Reply, Loader2, MessageCircle, ChevronDown } from "lucide-react";
import { useAuth } from "@/features/auth";

const API = API_URL;

interface CommentData {
  id: string;
  user_id: string;
  parent_id: string | null;
  body: string | null;
  is_deleted: boolean;
  author_username: string | null;
  author_avatar_url: string | null;
  created_at: string;
  reply_count: number;
}

interface CommentPanelProps {
  panoramaId: string;
  onClose: () => void;
}

function timeAgo(iso: string) {
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60)  return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

function Avatar({ username, url }: { username: string | null; url: string | null }) {
  const name = username ?? "?";
  return url ? (
    <img src={url} alt={name} className="w-7 h-7 rounded-full object-cover shrink-0" />
  ) : (
    <div className="w-7 h-7 rounded-full bg-tone-700 flex items-center justify-center text-xs font-bold text-ink shrink-0 uppercase">
      {name[0]}
    </div>
  );
}

// ── Single comment row ────────────────────────────────────────────────────────

interface CommentRowProps {
  comment: CommentData;
  panoramaId: string;
  depth: number;
  onReply: (id: string, username: string) => void;
}

function CommentRow({ comment, panoramaId, depth, onReply }: CommentRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [replies, setReplies] = useState<CommentData[]>([]);
  const [loadingReplies, setLoadingReplies] = useState(false);

  const loadReplies = async () => {
    if (expanded) { setExpanded(false); return; }
    setLoadingReplies(true);
    try {
      const res = await fetch(
        `${API}/api/panoramas/${panoramaId}/comments?parent_id=${comment.id}&limit=50`,
        { credentials: "include" },
      );
      if (res.ok) setReplies(await res.json());
    } finally {
      setLoadingReplies(false);
      setExpanded(true);
    }
  };

  if (comment.is_deleted) {
    return (
      <div className={`flex gap-2.5 ${depth > 0 ? "pl-8" : ""}`}>
        <div className="w-7 h-7 rounded-full bg-tone-800 shrink-0" />
        <p className="text-tone-600 text-sm italic pt-1">[comment removed]</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-2 ${depth > 0 ? "pl-8 border-l border-tone-800" : ""}`}>
      <div className="flex gap-2.5">
        <Avatar username={comment.author_username} url={comment.author_avatar_url} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-ink text-xs font-semibold">{comment.author_username ?? "Unknown"}</span>
            <span className="text-tone-500 text-xs">{timeAgo(comment.created_at)}</span>
          </div>
          <p className="text-tone-300 text-sm mt-0.5 leading-relaxed break-words">{comment.body}</p>
          {depth === 0 && (
            <div className="flex items-center gap-3 mt-1.5">
              {comment.reply_count > 0 && (
                <button
                  onClick={loadReplies}
                  className="flex items-center gap-1 text-xs text-tone-500 hover:text-indigo-400 transition-colors"
                >
                  {loadingReplies
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? "rotate-180" : ""}`} />}
                  {comment.reply_count} {comment.reply_count === 1 ? "reply" : "replies"}
                </button>
              )}
              <button
                onClick={() => onReply(comment.id, comment.author_username ?? "User")}
                className="flex items-center gap-1 text-xs text-tone-500 hover:text-ink transition-colors"
              >
                <Reply className="w-3 h-3" /> Reply
              </button>
            </div>
          )}
        </div>
      </div>
      {expanded && replies.map((r) => (
        <CommentRow key={r.id} comment={r} panoramaId={panoramaId} depth={depth + 1} onReply={onReply} />
      ))}
    </div>
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────

export default function CommentPanel({ panoramaId, onClose }: CommentPanelProps) {
  const { user } = useAuth();
  const [comments, setComments] = useState<CommentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [body, setBody] = useState("");
  const [replyTo, setReplyTo] = useState<{ id: string; username: string } | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const LIMIT = 20;

  const loadComments = useCallback(async (off = 0) => {
    setLoading(off === 0);
    try {
      const res = await fetch(
        `${API}/api/panoramas/${panoramaId}/comments?limit=${LIMIT}&offset=${off}`,
        { credentials: "include" },
      );
      if (!res.ok) return;
      const data: CommentData[] = await res.json();
      setComments((prev) => off === 0 ? data : [...prev, ...data]);
      setHasMore(data.length === LIMIT);
      setOffset(off + data.length);
    } finally {
      setLoading(false);
    }
  }, [panoramaId]);

  useEffect(() => { loadComments(0); }, [loadComments]);

  const handleReply = (id: string, username: string) => {
    setReplyTo({ id, username });
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const submit = async () => {
    if (!body.trim() || !user) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/panoramas/${panoramaId}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ body: body.trim(), parent_id: replyTo?.id ?? null }),
      });
      if (res.ok) {
        setBody("");
        setReplyTo(null);
        loadComments(0);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      className="fixed top-0 right-0 bottom-0 z-[180] w-full sm:w-96 bg-tone-950/95 backdrop-blur-xl border-l border-ink/8 flex flex-col shadow-2xl"
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ type: "spring", damping: 28, stiffness: 280 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3.5 border-b border-ink/8 shrink-0">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-tone-400" />
          <span className="text-ink font-semibold text-sm">Comments</span>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg text-tone-500 hover:text-ink transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Comments list */}
      <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-4">
        {loading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-tone-500" />
          </div>
        )}

        {!loading && comments.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-tone-600 gap-2">
            <MessageCircle className="w-8 h-8" />
            <p className="text-sm">No comments yet. Be the first!</p>
          </div>
        )}

        {comments.map((c) => (
          <CommentRow key={c.id} comment={c} panoramaId={panoramaId} depth={0} onReply={handleReply} />
        ))}

        {hasMore && (
          <button
            onClick={() => loadComments(offset)}
            className="text-xs text-tone-500 hover:text-ink text-center py-2"
          >
            Load more
          </button>
        )}
      </div>

      {/* Compose box */}
      <div className="shrink-0 border-t border-ink/8 px-4 py-3">
        {replyTo && (
          <div className="flex items-center justify-between mb-2 bg-indigo-500/10 border border-indigo-500/20 rounded-lg px-3 py-1.5">
            <span className="text-indigo-400 text-xs">Replying to <strong>@{replyTo.username}</strong></span>
            <button onClick={() => setReplyTo(null)} className="text-indigo-400/60 hover:text-indigo-300">
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
        {user ? (
          <div className="flex gap-2 items-end">
            <textarea
              ref={inputRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
              placeholder="Add a comment…"
              rows={2}
              maxLength={2000}
              className="flex-1 bg-tone-900 border border-tone-800 rounded-xl px-3 py-2.5 text-ink text-sm placeholder:text-tone-600 resize-none focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            <button
              onClick={submit}
              disabled={submitting || !body.trim()}
              className="p-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
            >
              {submitting
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Send className="w-4 h-4" />}
            </button>
          </div>
        ) : (
          <p className="text-tone-500 text-xs text-center py-2">Sign in to comment</p>
        )}
      </div>
    </motion.div>
  );
}
